import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import {
  BuildingItemDto,
  EquipmentItemDto,
  MaintenanceTimerCompletionFilterDto,
  MaintenanceTimerCompletionItemDto,
  MaintenanceTimerCompletionPdfService,
  MaintenanceTimerCompletionService,
  SortDto,
} from '@artemis-software/wr-api';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrentUserService } from '@services/current-user.service';
import { queryParam } from '@/utils/signals/router';
import { createForm } from '@sonofabit/ng-core';
import { DateRangeObject } from '@models/date-range';
import { toSelectedRange } from '@/utils/filter-range-utils';
import { BehaviorSubject, firstValueFrom, throwError, timeout } from 'rxjs';
import { tryWithLoading } from '@/utils/async-utils';
import { catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

interface MaintenanceTimerHistoryFilterValue {
  buildingId?: BuildingItemDto | null;
  buildingSectionName?: string | null;
  equipmentId?: EquipmentItemDto | null;
  dateRange?: DateRangeObject | null;
  finishedInTime?: boolean | null;
  overdueDaysMin?: number | null;
  page?: number | null;
  size?: number | null;
  sort?: SortDto | null;
}

const EXTERNAL_ROUTE_PREFIX = '/external/';

@Component({
  standalone: false,
  selector: 'wr-maintenance-timer-history-overview-page',
  templateUrl: './maintenance-timer-history-overview-page.component.html',
  styleUrls: ['./maintenance-timer-history-overview-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceTimerHistoryOverviewPageComponent {

  readonly maintenanceTimerCompletionService = inject(MaintenanceTimerCompletionService);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly currentUserService = inject(CurrentUserService);
  readonly filterForm = this.createForm();
  readonly maintenanceTimerId = queryParam('maintenanceTimerId');
  readonly maintenanceTimerCompletionPdfService = inject(MaintenanceTimerCompletionPdfService);
  private readonly http = inject(HttpClient);

  readonly loading$ = new BehaviorSubject<boolean>(false);

  readonly defaultSort: SortDto = { sort: 'maintenanceDate', order: SortDto.OrderEnum.Desc };

  constructor() {
    effect(() => {
      const id = this.maintenanceTimerId();
      this.filterForm.controls.maintenanceTimerId.setValue(id);
    });
  }

  private createForm() {
    return createForm(({ field }) => {
      return {
        maintenanceTimerId: field({
          value: null as string | null,
        }),
        buildingId: field({
          value: null as BuildingItemDto | null,
          toDto: value => value?.id,
        }),
        buildingSectionName: field({
          value: null as string | null,
        }),
        equipmentId: field({
          value: null as EquipmentItemDto | null,
          toDto: value => value?.id,
        }),
        dateRange: field({
          value: null as DateRangeObject | null,
        }),
        finishedInTime: field({
          value: null as boolean | null,
        }),
        overdueDaysMin: field({
          value: null as number | null,
        }),
        currentTenantId: field({
          value: null as string | null,
        }),
        page: field({
          value: 0 as number | null,
        }),
        size: field({
          value: 20 as number | null,
        })
      };
    });
  }

  private mapFilter(filter: MaintenanceTimerHistoryFilterValue): MaintenanceTimerCompletionFilterDto {
    const id = this.maintenanceTimerId();

    return {
      maintenanceTimerId: id ?? undefined,
      buildingId: filter.buildingId?.id,
      buildingSectionName: filter.buildingSectionName ?? undefined,
      equipmentId: filter.equipmentId?.id,
      dateRange: toSelectedRange(filter.dateRange),
      finishedInTime: filter.finishedInTime ?? undefined,
      overdueDaysMin: filter.overdueDaysMin ?? undefined,
      page: filter.page ?? 0,
      size: filter.size ?? 20,
      sort: filter.sort ?? undefined,
    };
  }

  readonly loadMaintenanceTimerHistories = (filter: MaintenanceTimerHistoryFilterValue) => {
    return this.maintenanceTimerCompletionService.findAll(this.mapFilter(filter));
  };

  readonly getCount = (filter: MaintenanceTimerHistoryFilterValue) =>
    this.maintenanceTimerCompletionService.getCount(this.mapFilter(filter));

  navigateToHistoryDetail(maintenanceHistory: MaintenanceTimerCompletionItemDto): void {
    const externalScope = this.router.url.startsWith(EXTERNAL_ROUTE_PREFIX) ? ['external'] : [];
    this.router.navigate([...externalScope, 'maintenanceTimers', 'historyDetail', maintenanceHistory.id]);
  }

  clearMaintenanceTimerId() {
    this.router.navigate([], {
      queryParams: { maintenanceTimerId: null },
      queryParamsHandling: 'merge',
    });
  }

  onDownloadClick(completionId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.generatePdf(completionId);
  }

  onProtocolDownloadClick(protocolId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.downloadProtocolPdf(protocolId);
  }

  async generatePdf(completionId: string): Promise<void> {
    await tryWithLoading(this.loading$, async () => {
      try {
        const blob = await firstValueFrom(
          this.maintenanceTimerCompletionPdfService.generatePdf({ maintenanceTimerCompletionId: completionId }).pipe(
            timeout(60000),
            catchError(error => {
              console.error(
                error.name === 'TimeoutError'
                  ? 'Request timed out. Please try again.'
                  : 'An error occurred:',
                error.message
              );
              return throwError(() => error);
            })
          )
        );

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `maintenance-history-${completionId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to generate or download PDF:', error);
      }
    });
  }

  async downloadProtocolPdf(protocolId: string): Promise<void> {
    await tryWithLoading(this.loading$, async () => {
      try {
        const blob = await firstValueFrom(
          this.http.get(`${environment.apiUrl}/api/protocol/${protocolId}/exportPdf`, {
            responseType: 'blob',
          }).pipe(
            timeout(60000),
            catchError(error => throwError(() => error))
          )
        );

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `protocol-${protocolId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to download protocol PDF:', error);
      }
    });
  }
}
